import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, defer, from, lastValueFrom, of } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
    Adapter,
    AdapterStatus,
    AdapterSummaryStats,
    AdapterTestCase,
    AdapterVersion,
    CbcmSchemaSection,
    TransformationResult
} from './adapters.model';
import { MOCK_ADAPTER_STATS, MOCK_ADAPTERS, MOCK_CBCM_SCHEMA, SAMPLE_REGIMEN_BY_ID, TRIFOUR_DEMO_HIDDEN_ADAPTER_IDS, TRIFOUR_DEMO_MODE } from './adapters.mock';
import { formatAdapterOutput, getByPath } from './adapters.utils';

function cloneAdapters(source: Adapter[]): Adapter[] {
    return structuredClone(source);
}

function uid(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function nextPatchVersion(current: string): string {
    const parts = current.split('.').map((p) => parseInt(p, 10));
    while (parts.length < 3) parts.push(0);
    parts[2] = (parts[2] || 0) + 1;
    return parts.slice(0, 3).join('.');
}

@Injectable({ providedIn: 'root' })
export class AdaptersService {
    private readonly adaptersSubject = new BehaviorSubject<Adapter[]>(cloneAdapters(MOCK_ADAPTERS));

    /** Snapshot label for breadcrumbs without subscribing */
    getAdapterNameSnapshot(id: string): string | undefined {
        return this.adaptersSubject.value.find((a) => a.id === id)?.name;
    }

    getAll(): Observable<Adapter[]> {
        return this.adaptersSubject.pipe(map((list) => this.filterDemoView(list)));
    }

    getById(id: string): Observable<Adapter | undefined> {
        return this.adaptersSubject.pipe(map((list) => list.find((a) => a.id === id)));
    }

    getStats(): Observable<AdapterSummaryStats> {
        return this.adaptersSubject.pipe(
            map((adapters) => {
                const visible = this.filterDemoView(adapters);
                return {
                    totalAdapters: visible.length,
                    activeAdapters: visible.filter((a) => a.status === 'Active').length,
                    draftAdapters: visible.filter((a) => a.status === 'Draft').length,
                    failingTests: visible.reduce((sum, a) => sum + a.testCases.filter((t) => t.status === 'Failing').length, 0),
                    totalRegimensDeployed: visible.reduce((sum, a) => sum + a.regimensDeployed, 0)
                };
            })
        );
    }

    /** Static baseline matching seeded mock (used if stats observable not wired). */
    getBaselineStats(): AdapterSummaryStats {
        return { ...MOCK_ADAPTER_STATS };
    }

    getSchema(): Observable<CbcmSchemaSection[]> {
        return of(MOCK_CBCM_SCHEMA);
    }

    async evaluateJsonata(expression: string, data: unknown): Promise<unknown> {
        const { default: jsonata } = await import('jsonata');
        const expr = jsonata(expression);
        return expr.evaluate(data);
    }

    runTransformation(expression: string, regimenData: unknown, outputFormat: Adapter['outputFormat']): Observable<TransformationResult> {
        return defer(() =>
            from(
                (async (): Promise<TransformationResult> => {
                    const start = Date.now();
                    try {
                        const output = await this.evaluateJsonata(expression, regimenData);
                        const executionTimeMs = Date.now() - start;
                        const formatted = formatAdapterOutput(output, outputFormat);
                        return {
                            success: true,
                            output: formatted,
                            outputFormat,
                            executionTimeMs
                        };
                    } catch (error: unknown) {
                        const err = error as { message?: string; position?: number };
                        return {
                            success: false,
                            output: '',
                            outputFormat,
                            executionTimeMs: Date.now() - start,
                            errors: [
                                {
                                    message: err.message ?? 'Unknown error',
                                    position: err.position,
                                    type: 'syntax'
                                }
                            ]
                        };
                    }
                })()
            )
        );
    }

    runTestCase(adapterId: string, testCaseId: string): Observable<AdapterTestCase> {
        return defer(() =>
            from(
                (async (): Promise<AdapterTestCase> => {
                    const adapter = this.adaptersSubject.value.find((a) => a.id === adapterId);
                    if (!adapter) throw new Error('Adapter not found');
                    const tc = adapter.testCases.find((t) => t.id === testCaseId);
                    if (!tc) throw new Error('Test case not found');
                    const regimen =
                        SAMPLE_REGIMEN_BY_ID[tc.sampleRegimenId] ?? SAMPLE_REGIMEN_BY_ID['rrg-mfolfox6-cco-2023'];
                    const start = Date.now();
                    try {
                        const raw = await this.evaluateJsonata(adapter.currentExpression, regimen);
                        const actual = getByPath(raw, tc.expectedOutputPath);
                        const pass = String(actual ?? '') === tc.expectedOutputValue;
                        const duration = Date.now() - start;
                        const updated: AdapterTestCase = {
                            ...tc,
                            status: pass ? 'Passing' : 'Failing',
                            lastRunOn: new Date(),
                            lastRunDuration: duration,
                            errorMessage: pass ? undefined : `Expected "${tc.expectedOutputValue}", got "${String(actual ?? '')}"`
                        };
                        this.patchAdapter(adapter.id, (draft) => {
                            draft.testCases = draft.testCases.map((x) => (x.id === tc.id ? updated : x));
                            this.recomputeAggregateTests(draft);
                        });
                        return updated;
                    } catch (error: unknown) {
                        const e = error as Error;
                        const updated: AdapterTestCase = {
                            ...tc,
                            status: 'Failing',
                            lastRunOn: new Date(),
                            lastRunDuration: Date.now() - start,
                            errorMessage: e.message
                        };
                        this.patchAdapter(adapter.id, (draft) => {
                            draft.testCases = draft.testCases.map((x) => (x.id === tc.id ? updated : x));
                            this.recomputeAggregateTests(draft);
                        });
                        return updated;
                    }
                })()
            )
        );
    }

    runAllTests(adapterId: string): Observable<AdapterTestCase[]> {
        return defer(() =>
            from(
                (async (): Promise<AdapterTestCase[]> => {
                    const adapter = this.adaptersSubject.value.find((a) => a.id === adapterId);
                    if (!adapter) return [];
                    for (const tc of adapter.testCases) {
                        await lastValueFrom(this.runTestCase(adapterId, tc.id));
                    }
                    return this.adaptersSubject.value.find((a) => a.id === adapterId)?.testCases ?? [];
                })()
            )
        );
    }

    saveVersion(adapterId: string, expression: string, changelog: string): Observable<AdapterVersion> {
        const adapter = this.adaptersSubject.value.find((a) => a.id === adapterId);
        if (!adapter) throw new Error('Adapter not found');
        const versionNumber = nextPatchVersion(adapter.currentVersion);
        const nv: AdapterVersion = {
            id: uid('v'),
            versionNumber,
            expression,
            createdBy: 'HIS Admin',
            createdOn: new Date(),
            status: adapter.status === 'Active' ? 'Draft' : adapter.status,
            changelog,
            testStatus: adapter.testStatus,
            testsPassing: adapter.testsPassing,
            testsTotal: adapter.testsTotal,
            isActive: true
        };
        this.patchAdapter(adapterId, (draft) => {
            draft.versions.forEach((v) => {
                v.isActive = false;
            });
            draft.versions.unshift(nv);
            draft.currentVersion = versionNumber;
            draft.currentExpression = expression;
            draft.lastUpdatedBy = 'HIS Admin';
            draft.lastUpdatedOn = new Date();
        });
        return of(nv);
    }

    activateVersion(adapterId: string, versionId: string): Observable<void> {
        this.patchAdapter(adapterId, (draft) => {
            const v = draft.versions.find((x) => x.id === versionId);
            if (!v) return;
            draft.versions.forEach((x) => {
                x.isActive = x.id === versionId;
            });
            draft.currentExpression = v.expression;
            draft.currentVersion = v.versionNumber;
            draft.lastUpdatedBy = 'HIS Admin';
            draft.lastUpdatedOn = new Date();
        });
        return of(undefined);
    }

    restoreVersionAsDraft(adapterId: string, versionId: string): Observable<AdapterVersion> {
        const adapter = this.adaptersSubject.value.find((a) => a.id === adapterId);
        if (!adapter) throw new Error('Adapter not found');
        const v = adapter.versions.find((x) => x.id === versionId);
        if (!v) throw new Error('Version not found');
        return this.saveVersion(adapterId, v.expression, `Restored from v${v.versionNumber} as new draft`);
    }

    createAdapter(partial: Partial<Adapter>): Observable<Adapter> {
        const id = partial.id ?? uid('adapter');
        const created: Adapter = {
            id,
            name: partial.name ?? 'New adapter',
            description: partial.description ?? '',
            targetSystem: partial.targetSystem ?? 'Custom',
            outputFormat: partial.outputFormat ?? 'JSON',
            status: partial.status ?? 'Draft',
            currentVersion: partial.currentVersion ?? '0.1.0',
            currentExpression: partial.currentExpression ?? '{}',
            regimensDeployed: partial.regimensDeployed ?? 0,
            testStatus: partial.testStatus ?? 'NotTested',
            testsPassing: partial.testsPassing ?? 0,
            testsTotal: partial.testsTotal ?? 0,
            versions: partial.versions ?? [],
            testCases: partial.testCases ?? [],
            createdBy: partial.createdBy ?? 'HIS Admin',
            createdOn: partial.createdOn ?? new Date(),
            lastUpdatedBy: partial.lastUpdatedBy ?? 'HIS Admin',
            lastUpdatedOn: partial.lastUpdatedOn ?? new Date(),
            tags: partial.tags ?? []
        };
        if (created.versions.length === 0) {
            created.versions.push({
                id: uid('v'),
                versionNumber: created.currentVersion,
                expression: created.currentExpression,
                createdBy: created.createdBy,
                createdOn: created.createdOn,
                status: created.status,
                changelog: 'Initial version',
                testStatus: 'NotTested',
                testsPassing: 0,
                testsTotal: 0,
                isActive: true
            });
        }
        const next = [...this.adaptersSubject.value, created];
        this.adaptersSubject.next(next);
        return of(created);
    }

    updateStatus(adapterId: string, status: AdapterStatus): Observable<void> {
        this.patchAdapter(adapterId, (draft) => {
            draft.status = status;
            draft.lastUpdatedOn = new Date();
        });
        return of(undefined);
    }

    duplicateAdapter(adapterId: string): Observable<Adapter> {
        const src = this.adaptersSubject.value.find((a) => a.id === adapterId);
        if (!src) throw new Error('Adapter not found');
        const copy = structuredClone(src);
        copy.id = uid('adapter');
        copy.name = `${copy.name} (copy)`;
        copy.regimensDeployed = 0;
        copy.createdOn = new Date();
        copy.lastUpdatedOn = new Date();
        copy.testCases = copy.testCases.map((t) => ({ ...t, id: uid('tc'), status: 'NotTested' as const }));
        copy.versions = copy.versions.map((v) => ({ ...v, id: uid('v'), isActive: false }));
        if (copy.versions[0]) copy.versions[0].isActive = true;
        const next = [...this.adaptersSubject.value, copy];
        this.adaptersSubject.next(next);
        return of(copy);
    }

    upsertTestCase(adapterId: string, testCase: AdapterTestCase): Observable<void> {
        this.patchAdapter(adapterId, (draft) => {
            const i = draft.testCases.findIndex((t) => t.id === testCase.id);
            if (i >= 0) draft.testCases[i] = testCase;
            else draft.testCases.push(testCase);
            this.recomputeAggregateTests(draft);
        });
        return of(undefined);
    }

    deleteTestCase(adapterId: string, testCaseId: string): Observable<void> {
        this.patchAdapter(adapterId, (draft) => {
            draft.testCases = draft.testCases.filter((t) => t.id !== testCaseId);
            this.recomputeAggregateTests(draft);
        });
        return of(undefined);
    }

    private patchAdapter(id: string, fn: (a: Adapter) => void): void {
        const list = this.adaptersSubject.value.map((a) => (a.id === id ? structuredClone(a) : a));
        const idx = list.findIndex((a) => a.id === id);
        if (idx < 0) return;
        fn(list[idx]);
        this.adaptersSubject.next(list);
    }

    private recomputeAggregateTests(draft: Adapter): void {
        const total = draft.testCases.length;
        const passing = draft.testCases.filter((t) => t.status === 'Passing').length;
        draft.testsTotal = total;
        draft.testsPassing = passing;
        if (total === 0) draft.testStatus = 'NotTested';
        else if (passing === total) draft.testStatus = 'Passing';
        else draft.testStatus = 'Failing';
        draft.lastUpdatedOn = new Date();
    }

    private filterDemoView(adapters: Adapter[]): Adapter[] {
        if (!TRIFOUR_DEMO_MODE) return adapters;
        const hidden = new Set<string>(TRIFOUR_DEMO_HIDDEN_ADAPTER_IDS);
        return adapters.filter((a) => !hidden.has(a.id));
    }
}
